<template>
  <van-popup v-bind="$attrs" position="center" class="popup-l-r">
    <div class="popup-view">
      <h3 class="forget-text1 text-[15px] text-center">
        {{ $t('forget-password-index.title') }}
      </h3>
      <van-form @submit="onSubmit">
        <div class="from-item rounded-md">
          <!-- 账户模式输入框 -->
          <van-field
            v-if="checked == 'username'"
            ref="refUsernameInput"
            v-model="form.userIdentifier"
            :placeholder="$t('forget-password-index.placeholder')"
            class="custom-label mt20px"
            :border="true"
            :clearable="true"
            @input="handleInputChange"
            @blur="validateUserIdentifier"
          >
            <template #left-icon>
              <div class="icon">
                <img class="w15px h15px mr5px" :src="getImageUrlWithCDN_TEST('/login/user.png')" />
              </div>
            </template>
          </van-field>

          <!-- 手机号模式输入框 -->
          <van-field
            v-else
            ref="refPhoneInput"
            v-model="form.userIdentifier"
            :placeholder="$t('forget-password-index.placeholder')"
            class="custom-label mt20px"
            :border="true"
            :clearable="true"
            @input="handleInputChange"
            @blur="validateUserIdentifier"
          >
            <template #left-icon>
              <div class="flex items-center">
                <img
                  v-if="isBrazil"
                  class="w18px h13.5px mr5px"
                  :src="getImageUrlWithCDN_TEST('/login/login-pt.png', 'local')"
                />
                <img
                  v-else
                  class="w18px h13.5px mr5px"
                  :src="getImageUrlWithCDN_TEST('/login/login-pt-mx.png', 'local')"
                />
                <span class="forget-text1">{{ $t('forget-password-index.phoneCode') }}</span>
                <span class="inline-block w1px h25px ml10px mr5px"></span>
              </div>
            </template>
          </van-field>

          <div v-if="errorMessages.userIdentifier" class="error-message">
            {{ errorMessages.userIdentifier }}
          </div>

          <van-radio-group
            v-show="showRadioGroup"
            v-model="checked"
            shape="square"
            icon-size="16"
            class="flex items-center justify-between"
          >
            <van-radio name="phone" checked-color="var(--radio-bg-color)">
              <span class="text-11px forget-text2">{{ $t('forget-password-index.isPhone') }}</span>
            </van-radio>
            <van-radio name="username" checked-color="var(--radio-bg-color)">
              <span class="text-11px forget-text2">
                {{ $t('forget-password-index.isAccount') }}
              </span>
            </van-radio>
          </van-radio-group>
        </div>

        <div class="btn-container mt20px">
          <van-button
            class="h33px rounded-[7px] but-submit"
            :loading="loading"
            native-type="submit"
            block
          >
            {{ $t('forget-password-index.next') }}
          </van-button>
        </div>
      </van-form>
    </div>
    <div class="close-container" @click="closePopup">
      <img
        :src="getImageUrlWithCDN_TEST('/login/close-f.png', 'local')"
        :alt="$t('forget-password-index.logoAlt')"
        class="close-icon"
      />
    </div>
  </van-popup>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { isBrazil } from '@/utils/i18n'
import { checkUserApi } from '@/api/user'
defineOptions({
  inheritAttrs: false,
})
const { t } = useI18n()
const emit = defineEmits(['close'])
// /user/checkUser
const form = ref({
  userIdentifier: '',
})
const errorMessages = ref({
  userIdentifier: '',
})

// 默认设置为账户模式
const checked = ref('username')
const hasStartedTyping = ref({
  userIdentifier: false,
})
const loading = ref(false)

const refUsernameInput = ref()
const refPhoneInput = ref()
// 控制单选框组显示
const showRadioGroup = ref(false)

// 巴西手机号正则：10-11位数字，以合适的开头数字[1-9]
const phoneRegex = /^[1-9][0-9]{9,10}$/

// 账户名正则：4-16个字符，英文/数字
const usernameRegex = /^[a-zA-Z0-9]{4,16}$/

// 检测输入内容并动态控制单选框显示
const checkInputType = () => {
  const value = form.value.userIdentifier.trim()

  if (!value) {
    // 输入为空时隐藏单选框，默认为账户
    showRadioGroup.value = false
    checked.value = 'username'
    return
  }

  const isPhoneValid = phoneRegex.test(value)
  const isUsernameValid = usernameRegex.test(value)
  const isInPhoneLengthRange = value.length >= 6 && value.length <= 12
  const isSixDigitPhoneLike = isInPhoneLengthRange && /^\d{6,12}$/.test(value)
  if (isPhoneValid && isUsernameValid) {
    showRadioGroup.value = true
    if (checked.value == 'phone') {
      nextTick(() => {
        refPhoneInput.value.focus()
      })
    } else {
      nextTick(() => {
        refUsernameInput.value.focus()
      })
    }
  } else if (isPhoneValid || isSixDigitPhoneLike) {
    showRadioGroup.value = true
    checked.value = 'phone'
    nextTick(() => {
      refPhoneInput.value.focus()
    })
  } else if (isUsernameValid) {
    showRadioGroup.value = false
    checked.value = 'username'
    nextTick(() => {
      refUsernameInput.value.focus()
    })
  } else {
    showRadioGroup.value = false
    checked.value = 'username'
    nextTick(() => {
      refUsernameInput.value.focus()
    })
  }
}

// 手机号和账户校验函数
const validateUserIdentifier = () => {
  hasStartedTyping.value.userIdentifier = true
  const value = form.value.userIdentifier.trim()

  if (!value) {
    errorMessages.value.userIdentifier = t('forget-password-index.errorEmpty')
    return false
  }

  if (checked.value === 'phone') {
    if (!phoneRegex.test(value)) {
      errorMessages.value.userIdentifier = t('forget-password-index.errorPhone')
      return false
    }
  } else {
    if (!usernameRegex.test(value)) {
      errorMessages.value.userIdentifier = t('forget-password-index.errorAccount')
      return false
    }
  }
  errorMessages.value.userIdentifier = ''
  return true
}

// 处理输入变化
const handleInputChange = () => {
  hasStartedTyping.value.userIdentifier = true
  checkInputType() // 检测输入类型并更新单选框显示
  handleInput() // 原有的防抖验证
}

// 实时校验（防抖处理）
let validateTimer = null
const handleInput = () => {
  clearTimeout(validateTimer)
  validateTimer = setTimeout(() => {
    if (hasStartedTyping.value.userIdentifier) {
      validateUserIdentifier()
    }
  }, 500)
}

// 切换单选框时重新校验
watch(checked, (newVal) => {
  errorMessages.value.userIdentifier = ''
  if (hasStartedTyping.value.userIdentifier) {
    setTimeout(() => validateUserIdentifier(), 100)
  }
})

const closePopup = () => {
  form.value.userIdentifier = ''
  errorMessages.value.userIdentifier = ''
  hasStartedTyping.value.userIdentifier = false
  // 重置单选框显示状态
  showRadioGroup.value = false
  checked.value = 'username'
  emit('close')
}

// 提交验证
const onSubmit = async () => {
  hasStartedTyping.value.userIdentifier = true

  if (validateUserIdentifier()) {
    loading.value = true
    try {
      const payload = {
        type: checked.value,
        value: form.value.userIdentifier,
      }
      const res = await checkUserApi(payload)
      if (res.data != 0) {
        // 跳转客服
      }
    } finally {
      loading.value = false
    }
  }
}
</script>
<style scoped lang="scss">
::v-deep(.van-radio__icon .van-icon) {
  border-radius: 4px;
}
.popup-l-r {
  width: 100%;
  border-radius: 8px;

  .popup-view {
    width: 100%;
    background-color: var(--popup-primary-bg);
    border: 1px solid var(--popup-primary-border);
    border-radius: 15px;
    padding: 15px;
  }
}

.close-container {
  margin-top: 12px;
  display: flex;
  justify-content: center;
  .close-icon {
    width: 28px;
    height: 28px;
  }
}

.from-item {
  position: relative;
}

.error-message {
  position: absolute;
  left: 15px;
  bottom: 0;
  transform: translateY(100%);
  color: var(--special-red-10);
  font-size: 11px;
}

.custom-label {
  --van-field-label-width: 28px;
  --van-cell-border-color: none;
  padding: 0 10px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  font-size: 12px;
  height: 35px;
  border-radius: 5px;
  --van-field-icon-size: 20px;
}

.forget-text1 {
  color: var(--popup-primary-title);
}

.forget-text2 {
  color: var(--popup-primary-title);
}
.but-submit {
  background-color: var(--button-primary-bg);
  border: none;
  color: var(--button-primary-text);
}
</style>
